import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { downloadCSV } from '../lib/exportUtils';
import {
  AlertTriangle, Search, Loader2, Download, RefreshCw, Filter,
  AlertCircle, Clock, XCircle, ChevronDown,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useActiveCompany } from '../hooks/useActiveCompany';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const EXCEPTION_TYPES = [
  { value: '', label: 'All Exceptions' },
  { value: 'missing_punch', label: 'Missing Punch' },
  { value: 'late_arrival', label: 'Late Arrival' },
  { value: 'early_exit', label: 'Early Exit' },
  { value: 'missing_out_punch', label: 'Missing Out Punch' },
  { value: 'unapproved_absence', label: 'Unapproved Absence (LOP)' },
];

const AttendanceExceptions = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useActiveCompany();
  const [period, setPeriod] = useState(() => {
    const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [exceptionType, setExceptionType] = useState('');
  const [exceptions, setExceptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchCompanies = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/companies`, { withCredentials: true });
      setCompanies(data);
      setSelectedCompany((cur) => (data.length > 0 && !cur ? data[0].id : cur));
    } catch (e) { console.error(e); }
  }, [setSelectedCompany]);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);
  const fetchExceptions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ company_id: selectedCompany, period });
      if (exceptionType) params.append('exception_type', exceptionType);
      const { data } = await axios.get(`${API_URL}/attendance/exceptions?${params}`, { withCredentials: true });
      setExceptions(data);
    } catch (e) { console.error(e); setExceptions([]); }
    finally { setLoading(false); }
  }, [exceptionType, period, selectedCompany]);

  useEffect(() => { if (selectedCompany) fetchExceptions(); }, [selectedCompany, period, exceptionType, fetchExceptions]);



  const filtered = exceptions.filter(ex =>
    !search || ex.employee_name?.toLowerCase().includes(search.toLowerCase()) || ex.employee_code?.toLowerCase().includes(search.toLowerCase())
  );

  const severityBadge = (sev) => {
    const map = {
      high: 'bg-red-50 text-red-700 border-red-200',
      medium: 'bg-amber-50 text-amber-700 border-amber-200',
      low: 'bg-blue-50 text-blue-700 border-blue-200',
    };
    return map[sev] || 'bg-slate-50 text-slate-600';
  };

  const typeIcon = (type) => {
    if (type === 'missing_punch' || type === 'missing_out_punch') return <XCircle className="w-4 h-4 text-red-500" />;
    if (type === 'late_arrival') return <Clock className="w-4 h-4 text-amber-500" />;
    if (type === 'early_exit') return <Clock className="w-4 h-4 text-blue-500" />;
    if (type === 'unapproved_absence') return <AlertCircle className="w-4 h-4 text-red-600" />;
    return <AlertTriangle className="w-4 h-4 text-slate-400" />;
  };

  // Stats
  const highCount = exceptions.filter(e => e.severity === 'high').length;
  const mediumCount = exceptions.filter(e => e.severity === 'medium').length;
  const lowCount = exceptions.filter(e => e.severity === 'low').length;

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="attendance-exceptions-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
              <AlertTriangle className="inline-block w-8 h-8 mr-2 mb-1" /> Attendance Exception Register
            </h1>
            <p className="mt-1 text-sm text-slate-600">Missing punches, late arrivals, early exits, and unapproved absences</p>
          </div>
          <Button variant="outline" data-testid="exceptions-export-btn" onClick={() => {
            if (filtered.length === 0) { toast.error('No exceptions to export'); return; }
            downloadCSV(filtered.map(e => ({
              Employee: e.employee_name, Code: e.employee_code, Date: e.date,
              Type: e.exception_type, Severity: e.severity, Description: e.description,
            })), `attendance_exceptions_${period}.csv`);
            toast.success('Exceptions exported');
          }}>
            <Download className="w-4 h-4 mr-2" /> Export
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <p className="text-xs font-semibold uppercase text-slate-500">Total Exceptions</p>
            <p className="text-2xl font-semibold text-slate-900 mt-1 font-mono" data-testid="total-exceptions-count">{exceptions.length}</p>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-xs font-semibold uppercase text-red-500">High Severity</p>
            <p className="text-2xl font-semibold text-red-700 mt-1 font-mono">{highCount}</p>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-xs font-semibold uppercase text-amber-500">Medium Severity</p>
            <p className="text-2xl font-semibold text-amber-700 mt-1 font-mono">{mediumCount}</p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-xs font-semibold uppercase text-blue-500">Low Severity</p>
            <p className="text-2xl font-semibold text-blue-700 mt-1 font-mono">{lowCount}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4 flex-wrap">
          <select value={selectedCompany} onChange={e => setSelectedCompany(e.target.value)} data-testid="exceptions-company-select"
            className="h-10 px-3 bg-white border border-slate-300 rounded-md text-sm">
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <input type="month" value={period} onChange={e => setPeriod(e.target.value)} data-testid="exceptions-period-select"
            className="h-10 px-3 bg-white border border-slate-300 rounded-md text-sm" />
          <select value={exceptionType} onChange={e => setExceptionType(e.target.value)} data-testid="exception-type-filter"
            className="h-10 px-3 bg-white border border-slate-300 rounded-md text-sm">
            {EXCEPTION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
            <input data-testid="exceptions-search" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search employee..." className="w-full h-10 pl-10 pr-4 bg-white border border-slate-300 rounded-md text-sm" />
          </div>
        </div>

        {/* Exception Table */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full" data-testid="exceptions-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Employee</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Code</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Date</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Exception</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Severity</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Description</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6" className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan="6" className="text-center py-12 text-slate-500">
                  <AlertTriangle className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                  <p>No exceptions found for this period</p>
                </td></tr>
              ) : filtered.map((ex, i) => (
                <tr key={i} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`exception-row-${i}`}>
                  <td className="py-3 px-4 text-sm font-medium text-slate-900">{ex.employee_name}</td>
                  <td className="py-3 px-4 text-sm font-mono text-slate-600">{ex.employee_code}</td>
                  <td className="py-3 px-4 text-sm text-slate-700">{ex.date}</td>
                  <td className="py-3 px-4 text-sm">
                    <div className="flex items-center gap-1.5">
                      {typeIcon(ex.exception_type)}
                      <span className="capitalize text-slate-700">{ex.exception_type?.replace(/_/g, ' ')}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${severityBadge(ex.severity)}`}>{ex.severity}</span>
                  </td>
                  <td className="py-3 px-4 text-sm text-slate-600">{ex.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </MainLayout>
  );
};

export default AttendanceExceptions;
